import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PaletteIcon, XIcon, MoonIcon, SunIcon, GlobeIcon } from 'lucide-react';
import { Language, translations } from '../utils/translations';
interface ThemeSwitcherProps {
  currentTheme: string;
  currentMode: string;
  currentLang: Language;
  onSelectTheme: (theme: string) => void;
  onSelectMode: (mode: string) => void;
  onSelectLang: (lang: Language) => void;
  isVisible: boolean;
  isDemoPopupVisible?: boolean;
  isDemoFormOpen?: boolean;
}
export function ThemeSwitcher({
  currentTheme,
  currentMode,
  currentLang,
  onSelectTheme,
  onSelectMode,
  onSelectLang,
  isVisible,
  isDemoPopupVisible,
  isDemoFormOpen
}: ThemeSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const t = translations[currentLang];
  const THEMES = [{
    id: 'gold',
    name: t.themeGold,
    color: '#D4AF37'
  }, {
    id: 'emerald',
    name: t.themeEmerald,
    color: '#10b981'
  }, {
    id: 'sapphire',
    name: t.themeSapphire,
    color: '#3b82f6'
  }, {
    id: 'platinum',
    name: t.themePlatinum,
    color: '#d4d4d8'
  }, {
    id: 'purple',
    name: t.themePurple,
    color: '#a855f7'
  }];

  // Calculate the Y offset based on the popup state
  // Base position is bottom-28 (112px). 
  // When popup is visible, we need to move up to clear it PLUS add a gap.
  const yOffset = isDemoPopupVisible ? isDemoFormOpen ? -336 : -196 : 0;
  return <>
      <AnimatePresence data-id="element-205" data-mp="14u9sjgwz1rj4">
        {(isVisible || isOpen) && <div className="fixed inset-x-0 bottom-28 z-40 flex justify-center pointer-events-none px-4 md:bottom-36" data-id="element-206" data-mp="18wwnhqoxvskj">
            <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: yOffset
        }} exit={{
          opacity: 0,
          y: 50
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="relative w-full max-w-[320px] pointer-events-auto" data-id="element-207" data-mp="1dvhrm17bawe9">
              <button onClick={() => setIsOpen(true)} className="flex w-full items-center justify-center gap-3 rounded-2xl border border-zinc-800 bg-zinc-950/95 py-4 px-6 text-[15px] font-bold text-zinc-50 shadow-2xl backdrop-blur-md transition-all hover:bg-zinc-900 hover:border-zinc-700 hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap" title={t.themeSwitcherTitle} data-id="element-208" data-mp="3kn8t9c5fkrt1">
                <img src="https://em-content.zobj.net/source/apple/391/artist-palette_1f3a8.png" alt="Palette" className="h-5 w-5 shrink-0 object-contain" data-id="element-209" data-mp="3hn792drbjr3i" />
                {t.themeSwitcherTitle}
              </button>
            </motion.div>
          </div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-210" data-mp="975isfw5vqfh">
        {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-211" data-mp="45ogcfy13vws">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsOpen(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-212" data-mp="j2paeq3p02yf" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="relative w-full max-w-[320px] overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-900 p-5 shadow-2xl" data-id="element-213" data-mp="xo30oclqcg6b">
            <div className="mb-4 flex items-center justify-between" data-id="element-214" data-mp="isetcel47cy7">
              <h3 className="text-[15px] font-bold text-zinc-50" data-id="element-215" data-mp="iurjvg4xuvmv">{t.themeSwitcherTitle}</h3>
              <button onClick={() => setIsOpen(false)} className="rounded-full p-1 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-50" data-id="element-216" data-mp="5334n3ze7r1o">
                <XIcon className="h-5 w-5" data-id="element-217" data-mp="3rajkjriyqr5q" />
              </button>
            </div>

            <div className="mb-4 space-y-2" data-id="element-218" data-mp="1durrnwpjf6">
              <p className="text-[12px] font-semibold uppercase tracking-wider text-zinc-500" data-id="element-219" data-mp="13ctc1f66pdw1">Мова / Language</p>
              <div className="flex rounded-xl border border-zinc-800 bg-zinc-950 p-1" data-id="element-220" data-mp="ydq5fqak2zly">
                <button onClick={() => onSelectLang('en')} className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-[13px] font-medium transition-colors ${currentLang === 'en' ? 'bg-zinc-800 text-zinc-50' : 'text-zinc-400 hover:text-zinc-300'}`} data-id="element-221" data-mp="tmq9ood6anl1">
                  EN
                </button>
                <button onClick={() => onSelectLang('uk')} className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-[13px] font-medium transition-colors ${currentLang === 'uk' ? 'bg-zinc-800 text-zinc-50' : 'text-zinc-400 hover:text-zinc-300'}`} data-id="element-222" data-mp="anvwvkx9rx7i">
                  UK
                </button>
                <button onClick={() => onSelectLang('ru')} className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-[13px] font-medium transition-colors ${currentLang === 'ru' ? 'bg-zinc-800 text-zinc-50' : 'text-zinc-400 hover:text-zinc-300'}`} data-id="element-223" data-mp="27nmlejluzdxb">
                  RU
                </button>
              </div>
            </div>

            <div className="mb-4 space-y-2" data-id="element-224" data-mp="1oq3dxkf1u3d0">
              <p className="text-[12px] font-semibold uppercase tracking-wider text-zinc-500" data-id="element-225" data-mp="1tma8kh1zvsfn">Тема</p>
              <div className="flex rounded-xl border border-zinc-800 bg-zinc-950 p-1" data-id="element-226" data-mp="1ylwwbkfg8r2p">
                <button onClick={() => onSelectMode('dark')} className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-[13px] font-medium transition-colors ${currentMode === 'dark' ? 'bg-zinc-800 text-zinc-50' : 'text-zinc-400 hover:text-zinc-300'}`} data-id="element-227" data-mp="23lsqkrych452">
                  <MoonIcon className="h-4 w-4" data-id="element-228" data-mp="1ezzzx8q342e9" /> {t.modeDark}
                </button>
                <button onClick={() => onSelectMode('light')} className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-[13px] font-medium transition-colors ${currentMode === 'light' ? 'bg-zinc-800 text-zinc-50' : 'text-zinc-400 hover:text-zinc-300'}`} data-id="element-229" data-mp="19yu1qvdnnmxc">
                  <SunIcon className="h-4 w-4" data-id="element-230" data-mp="r9nm5h85rbfr" /> {t.modeLight}
                </button>
              </div>
            </div>

            <div className="space-y-2" data-id="element-231" data-mp="151ah9dqd6dwy">
              <p className="text-[12px] font-semibold uppercase tracking-wider text-zinc-500" data-id="element-232" data-mp="109iyhpdg9chd">Колір</p>
              <div className="space-y-1" data-id="element-233" data-mp="cj6idr81w9fp">
                {THEMES.map(t => <button key={t.id} onClick={() => onSelectTheme(t.id)} className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium transition-colors hover:bg-zinc-800 ${currentTheme === t.id ? 'bg-zinc-800 text-zinc-50' : 'text-zinc-400'}`} data-id="element-234" data-mp="8i34395nilgl">
                    <div className="h-4 w-4 rounded-full shadow-inner ring-1 ring-zinc-500" style={{
                  backgroundColor: t.color
                }} data-id="element-235" data-mp="3pkh2jfbw05bt" />
                    <span className="flex-1 text-left" data-id="element-236" data-mp="m3qrgm1g7239">{t.name}</span>
                  </button>)}
              </div>
            </div>
          </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}